// components/CompanySummary.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiHome,
  FiDollarSign,
  FiTrendingUp,
  FiDownload,
  FiRefreshCw,
  FiFilter,
  FiArrowUp,
  FiArrowDown,
  FiXCircle,
  FiBarChart2,
  FiUsers,
  FiFileText,
  FiGrid,
  FiList,
  FiAlertCircle,
  FiClock,
  FiDatabase,
  FiWifi,
  FiWifiOff,
  FiMenu,
  FiX,
  FiEye,
  FiExternalLink,
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import Dexie from 'dexie';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';

// ==================== BASE DE DONNÉES & UTILITAIRES ====================
const db = new Dexie('CompanySummaryCache');
db.version(1).stores({
  companies: 'id, company_name, nif, rccm, total_ttc, cacheId',
  stats: 'id',
  cacheInfo: 'key',
  requestLog: '++id, timestamp, endpoint',
});

class RateLimiter {
  // ... (inchangé)
  constructor(maxRequests = 5, timeWindow = 60000) {
    this.maxRequests = maxRequests;
    this.timeWindow = timeWindow;
    this.requests = [];
    this.requestQueue = [];
    this.isProcessing = false;
  }

  async acquire() {
    return new Promise((resolve) => {
      this.requestQueue.push(resolve);
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    while (this.requestQueue.length > 0) {
      const now = Date.now();
      this.requests = this.requests.filter(time => now - time < this.timeWindow);

      if (this.requests.length < this.maxRequests) {
        const resolve = this.requestQueue.shift();
        this.requests.push(now);
        resolve();
      } else {
        const oldestRequest = this.requests[0];
        const waitTime = this.timeWindow - (now - oldestRequest);
        await new Promise(resolve => setTimeout(resolve, waitTime + 100));
      }
    }

    this.isProcessing = false;
  }
}

const fetchWithRetry = async (url, options = {}, retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      if (i === retries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
};

const rateLimiter = new RateLimiter(3, 60000);

const logRequest = async (endpoint) => { /* inchangé */ };
const isCacheValid = async () => { /* inchangé */ };
const saveToCache = async (companies, stats) => { /* inchangé */ };
const loadFromCache = async () => { /* inchangé */ };
const clearCache = async () => { /* inchangé */ };

// Fonctions utilitaires
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '0 FC';
  return new Intl.NumberFormat('fr-FR').format(amount) + ' FC';
};

const formatNumber = (number) => new Intl.NumberFormat('fr-FR').format(number);

const formatDate = (dateString) => {
  if (!dateString) return 'Jamais';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Date invalide';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date);
};

// ==================== COMPOSANTS RÉUTILISABLES ====================

// Badge (comme dans AdminTable)
const Badge = ({ children, color = 'blue' }) => {
  const colors = {
    red: 'bg-red-500/10 border-red-500/20 text-red-400',
    green: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${colors[color] || colors.blue}`}>
      {children}
    </span>
  );
};

// Carte entreprise mobile
const CompanyCardMobile = ({ company, onClick }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      layout
      className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-4 shadow-lg"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-xl bg-blue-500/10 border border-blue-400/20">
          <FiHome className="text-blue-400 text-lg" />
        </div>
        <div className="flex-1">
          <h3 className="text-white font-semibold text-sm">{company.company_name}</h3>
          <p className="text-slate-400 text-xs">NIF: {company.nif || '—'}</p>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="p-1 rounded-lg text-slate-400 hover:text-white">
          {expanded ? <FiChevronLeft size={16} /> : <FiChevronRight size={16} />}
        </button>
      </div>

      <div className="space-y-2 text-sm text-slate-300">
        <div className="flex justify-between">
          <span className="text-slate-500">Factures</span>
          <span>{formatNumber(company.invoice_count)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">CA TTC</span>
          <span className="text-emerald-400 font-semibold">{formatCurrency(company.total_ttc)}</span>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 pt-3 border-t border-white/10 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">RCCM</span><span className="text-slate-200">{company.rccm || '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Adresse</span><span className="text-slate-200">{company.address || '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Total HT</span><span className="text-slate-200">{formatCurrency(company.total_ht)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">TVA (16%)</span><span className="text-amber-400">{formatCurrency(company.tax_amount)}</span></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => onClick(company)}
        className="w-full mt-3 py-2 rounded-xl bg-blue-500/10 border border-blue-400/20 text-blue-400 text-xs font-medium flex items-center justify-center gap-1 hover:bg-blue-500/20 transition-colors"
      >
        <FiEye size={14} /> Détails
      </button>
    </motion.div>
  );
};

// Menu mobile (identique à AdminTable)
const MobileMenu = ({ isOpen, onClose, onRefresh, onClearCache, onExport, onResetFilters, isRefreshing }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={onClose}
        />
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed right-0 top-0 h-full w-72 bg-gradient-to-b from-blue-950/95 to-slate-900/95 backdrop-blur-xl border-l border-blue-400/20 shadow-2xl z-50 p-6"
        >
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-white font-bold text-lg">Menu</h3>
            <button onClick={onClose} className="p-2 rounded-xl bg-white/5 text-slate-400 hover:text-white"><FiX size={18} /></button>
          </div>
          <div className="space-y-4">
            <button onClick={onRefresh} disabled={isRefreshing} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 disabled:opacity-50">
              <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} /> {isRefreshing ? 'Mise à jour...' : 'Actualiser'}
            </button>
            <button onClick={onClearCache} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400">
              <FiDatabase /> Vider le cache
            </button>
            <button onClick={onExport} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-blue-500/10 border border-blue-400/20 text-blue-400">
              <FiDownload /> Exporter CSV
            </button>
            <button onClick={onResetFilters} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-purple-500/10 border border-purple-500/20 text-purple-400">
              <FiRefreshCw /> Réinitialiser filtres
            </button>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

// Filtres mobiles (identique à AdminTable mais pour montants)
const MobileFilters = ({ isOpen, onClose, minAmount, setMinAmount, maxAmount, setMaxAmount, onApply }) => {
  const [localMin, setLocalMin] = useState(minAmount);
  const [localMax, setLocalMax] = useState(maxAmount);

  const handleApply = () => {
    setMinAmount(localMin);
    setMaxAmount(localMax);
    onApply();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-blue-950/95 to-slate-900/95 backdrop-blur-xl border-t border-blue-400/20 rounded-t-2xl z-50 p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-white font-bold text-lg flex items-center gap-2"><FiFilter /> Filtres</h3>
              <button onClick={onClose} className="p-2 rounded-xl bg-white/5 text-slate-400"><FiX size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-slate-400 text-xs uppercase block mb-2">CA minimum (FC)</label>
                <input
                  type="number"
                  placeholder="0"
                  value={localMin}
                  onChange={(e) => setLocalMin(e.target.value)}
                  className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400/20"
                />
              </div>
              <div>
                <label className="text-slate-400 text-xs uppercase block mb-2">CA maximum (FC)</label>
                <input
                  type="number"
                  placeholder="10 000 000"
                  value={localMax}
                  onChange={(e) => setLocalMax(e.target.value)}
                  className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400/20"
                />
              </div>
              <button onClick={handleApply} className="w-full py-3 bg-blue-500/20 border border-blue-400/30 text-blue-400 rounded-xl font-medium hover:bg-blue-500/30 transition-colors">
                Appliquer les filtres
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ==================== COMPOSANT PRINCIPAL ====================
const CompanySummary = () => {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cacheDate, setCacheDate] = useState(null);

  // États pagination / recherche / tri
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'total_ttc', direction: 'desc' });
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' ou 'grid' (desktop)
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Mobile
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const lastFetchRef = useRef(null);
  const fetchTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Détection mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Connexion
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const debouncedFetch = useCallback((callback, delay = 1000) => {
    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    fetchTimeoutRef.current = setTimeout(callback, delay);
  }, []);

  // Fetch API (identique logique métier)
  const fetchFromAPI = useCallback(async (forceRefresh = false) => {
    // ... (même implémentation que précédemment, retourne companies + stats)
    // Je conserve la logique exacte pour éviter les régressions
    try {
      if (!forceRefresh && lastFetchRef.current) {
        const timeSinceLastFetch = Date.now() - lastFetchRef.current;
        if (timeSinceLastFetch < 10000) {
          const { companies: cachedCompanies, stats: cachedStats } = await loadFromCache();
          if (cachedCompanies && cachedCompanies.length > 0) {
            setCompanies(cachedCompanies);
            setStats(cachedStats);
            return;
          }
        }
      }

      await rateLimiter.acquire();
      await logRequest('get_company_summary.php');

      const response = await fetchWithRetry(`${API_CONFIG.apiUrl}get_company_summary.php`);
      const text = await response.text();

      if (!text || text.trim() === '') throw new Error('Réponse vide');

      let data;
      try { data = JSON.parse(text); } catch (e) { throw new Error('JSON invalide'); }
      if (data.success === false) throw new Error(data.message || 'Erreur serveur');

      const newCompanies = data.data || [];
      const newStats = data.stats || null;

      setCompanies(newCompanies);
      setStats(newStats);
      await saveToCache(newCompanies, newStats);
      lastFetchRef.current = Date.now();

      const cacheInfo = await db.cacheInfo.get('company_data');
      if (cacheInfo) setCacheDate(cacheInfo.timestamp);
    } catch (err) {
      if (err.message.includes('429') || err.status === 429) {
        setError("Trop de requêtes. Veuillez patienter.");
        setTimeout(() => fetchFromAPI(false), 30000);
      } else {
        const { companies: cachedCompanies, stats: cachedStats } = await loadFromCache();
        if (cachedCompanies && cachedCompanies.length > 0) {
          setCompanies(cachedCompanies);
          setStats(cachedStats);
          setError(null);
        } else {
          setError('Impossible de charger les données.');
        }
      }
    }
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const cacheValid = await isCacheValid();
      if (cacheValid) {
        const { companies: cachedCompanies, stats: cachedStats } = await loadFromCache();
        if (cachedCompanies && cachedCompanies.length > 0) {
          setCompanies(cachedCompanies);
          setStats(cachedStats);
          const cacheInfo = await db.cacheInfo.get('company_data');
          if (cacheInfo) setCacheDate(cacheInfo.timestamp);
          if (isOnline) debouncedFetch(() => fetchFromAPI(false), 15000);
          return;
        }
      }
      await fetchFromAPI(false);
    } catch (err) {
      const { companies: cachedCompanies, stats: cachedStats } = await loadFromCache();
      if (cachedCompanies && cachedCompanies.length > 0) {
        setCompanies(cachedCompanies);
        setStats(cachedStats);
        setError(null);
      } else {
        setError('Impossible de charger les données. Vérifiez votre connexion.');
      }
    } finally {
      setLoading(false);
    }
  };

  const manualRefresh = async () => {
    if (!isOnline) {
      setError('Pas de connexion internet.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (isRefreshing) return;
    setIsRefreshing(true);
    setError(null);
    try {
      await fetchFromAPI(true);
    } catch (err) {
      setError('Erreur lors du rafraîchissement');
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsRefreshing(false);
    }
  };

  const clearCacheAndReload = async () => {
    await clearCache();
    lastFetchRef.current = null;
    await fetchFromAPI(true);
  };

  useEffect(() => {
    loadData();
    return () => { if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current); };
  }, []);

  // Filtrage / tri avec debounce
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      let filtered = [...companies];
      if (searchTerm) {
        filtered = filtered.filter(company =>
          company.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          company.nif?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          company.rccm?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      if (minAmount) filtered = filtered.filter(company => company.total_ttc >= parseFloat(minAmount));
      if (maxAmount) filtered = filtered.filter(company => company.total_ttc <= parseFloat(maxAmount));

      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (['total_ht', 'tax_amount', 'total_ttc', 'invoice_count'].includes(sortConfig.key)) {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        }
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });

      setFilteredCompanies(filtered);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [companies, searchTerm, sortConfig, minAmount, maxAmount]);

  // Pagination
  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = indexOfFirstItem + itemsPerPage;
  const currentCompanies = filteredCompanies.slice(indexOfFirstItem, indexOfLastItem);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const exportToCSV = () => {
    const headers = ['Entreprise', 'NIF', 'RCCM', 'Adresse', 'Factures', 'Total HT', 'TVA', 'Total TTC'];
    const csvData = filteredCompanies.map(c => [
      c.company_name, c.nif || '', c.rccm || '', c.address || '',
      c.invoice_count, c.total_ht, c.tax_amount, c.total_ttc,
    ]);
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `entreprises_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setMinAmount('');
    setMaxAmount('');
    setSortConfig({ key: 'total_ttc', direction: 'desc' });
  };

  // Composant Modal (local, utilisant Tailwind)
  const CompanyDetailsModal = ({ company, onClose }) => {
    if (!company) return null;
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 50 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-gradient-to-b from-blue-950/95 to-slate-900/95 backdrop-blur-xl border border-blue-400/20 rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        >
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <FiHome className="text-blue-400" /> {company.company_name}
              </h2>
              <button onClick={onClose} className="p-2 rounded-xl bg-white/5 text-slate-400 hover:text-white"><FiX size={20} /></button>
            </div>
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Informations légales</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">NIF</span><p className="text-white">{company.nif || '—'}</p></div>
                  <div><span className="text-slate-500">RCCM</span><p className="text-white">{company.rccm || '—'}</p></div>
                  <div className="col-span-2"><span className="text-slate-500">Adresse</span><p className="text-white">{company.address || '—'}</p></div>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Performances financières</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <div className="text-xs text-slate-400 mb-1">Factures</div>
                    <div className="text-white font-bold">{formatNumber(company.invoice_count)}</div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <div className="text-xs text-slate-400 mb-1">Total HT</div>
                    <div className="text-white font-bold">{formatCurrency(company.total_ht)}</div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <div className="text-xs text-slate-400 mb-1">TVA (16%)</div>
                    <div className="text-amber-400 font-bold">{formatCurrency(company.tax_amount)}</div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <div className="text-xs text-slate-400 mb-1">CA TTC</div>
                    <div className="text-emerald-400 font-bold">{formatCurrency(company.total_ttc)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // ==================== RENDU ====================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950/50 to-indigo-950 relative">
      {/* Fond décoratif */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto p-3 md:p-6 pb-24 md:pb-6">
        {/* Barre d'info cache */}
        <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-3 md:p-4 mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-xs md:text-sm text-slate-300">
          <div className="flex items-center gap-2 flex-wrap">
            <FiDatabase className="text-blue-400" />
            <span>{cacheDate ? `📦 Données du ${formatDate(cacheDate)}` : '🔄 Données fraîches'}</span>
            <FiClock className="text-amber-400 ml-2" />
            <span>Cache 24h</span>
          </div>
          <button onClick={clearCacheAndReload} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-xl text-xs transition-colors">
            Vider cache
          </button>
        </div>

        {/* En-tête Desktop */}
        <div className="hidden md:block">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-6 mb-6"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                  Résumé des Entreprises
                </h1>
                <p className="text-slate-400 text-sm mt-1">Analyse financière et fiscale des entreprises</p>
              </div>
              <div className="flex gap-3">
                <button onClick={manualRefresh} disabled={isRefreshing} className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-xl text-sm disabled:opacity-50 hover:bg-amber-500/20 transition-colors">
                  <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} /> {isRefreshing ? 'Mise à jour...' : 'Actualiser'}
                </button>
                <button onClick={exportToCSV} className="flex items-center gap-2 px-4 py-2.5 bg-blue-500/10 border border-blue-400/20 text-blue-400 rounded-xl text-sm hover:bg-blue-500/20 transition-colors">
                  <FiDownload /> Exporter
                </button>
              </div>
            </div>

            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                {[
                  { label: 'Entreprises', value: stats.total_companies, icon: FiUsers, color: 'text-blue-400' },
                  { label: 'Factures', value: formatNumber(stats.total_invoices), icon: FiFileText, color: 'text-purple-400' },
                  { label: 'Chiffre d\'affaires', value: formatCurrency(stats.total_ttc), icon: FiDollarSign, color: 'text-emerald-400' },
                  { label: 'Moy. par entreprise', value: formatCurrency(stats.average_ttc), icon: FiTrendingUp, color: 'text-amber-400' },
                ].map((s, i) => (
                  <div key={i} className="bg-white/5 rounded-xl p-3 text-center border border-white/10">
                    <s.icon className={`mx-auto mb-1 ${s.color}`} size={20} />
                    <div className="text-white font-bold text-lg">{s.value}</div>
                    <div className="text-slate-400 text-xs">{s.label}</div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* Header Mobile */}
        <div className="md:hidden mb-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-white">Entreprises</h1>
            <p className="text-slate-400 text-xs">{filteredCompanies.length} entreprises</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setMobileFiltersOpen(true)} className="p-2 rounded-xl bg-white/5 border border-white/10 text-slate-300">
              <FiFilter size={18} />
            </button>
            <button onClick={() => setMobileMenuOpen(true)} className="p-2 rounded-xl bg-white/5 border border-white/10 text-slate-300">
              <FiMenu size={18} />
            </button>
          </div>
        </div>

        {/* Filtres et recherche Desktop */}
        <div className="hidden md:flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 min-w-[250px]">
            <FiSearch className="absolute left-4 top-3 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher par nom, NIF, RCCM..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-blue-400/20 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400/20"
            />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className="px-4 py-3 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded-xl text-sm flex items-center gap-2">
            <FiFilter /> Filtres
          </button>
          <button onClick={resetFilters} className="px-4 py-3 bg-white/5 border border-white/10 text-slate-300 rounded-xl text-sm flex items-center gap-2">
            <FiRefreshCw /> Réinitialiser
          </button>
          <div className="flex gap-1 bg-white/5 rounded-xl p-1 border border-white/10">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 rounded-lg text-sm ${viewMode === 'table' ? 'bg-blue-500/20 text-blue-400' : 'text-slate-400'}`}
            >
              <FiList size={16} />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1.5 rounded-lg text-sm ${viewMode === 'grid' ? 'bg-blue-500/20 text-blue-400' : 'text-slate-400'}`}
            >
              <FiGrid size={16} />
            </button>
          </div>
        </div>

        {/* Panneau de filtres Desktop */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mb-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white/5 border border-white/10 rounded-2xl">
                <div>
                  <label className="text-slate-400 text-xs uppercase block mb-2">CA minimum (FC)</label>
                  <input
                    type="number"
                    placeholder="0"
                    value={minAmount}
                    onChange={(e) => setMinAmount(e.target.value)}
                    className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-xs uppercase block mb-2">CA maximum (FC)</label>
                  <input
                    type="number"
                    placeholder="10 000 000"
                    value={maxAmount}
                    onChange={(e) => setMaxAmount(e.target.value)}
                    className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Barre de recherche mobile */}
        <div className="md:hidden mb-4">
          <div className="relative">
            <FiSearch className="absolute left-4 top-3 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-blue-400/20 rounded-2xl text-white placeholder-slate-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Contenu principal */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl overflow-hidden"
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-blue-400/20 border-t-blue-400 rounded-full animate-spin mb-4" />
              <p className="text-slate-300">Chargement...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <FiAlertCircle className="text-red-400 text-4xl mb-4" />
              <p className="text-red-400 mb-4">{error}</p>
              <button onClick={loadData} className="px-6 py-2 bg-blue-500/10 border border-blue-400/20 text-blue-400 rounded-xl hover:bg-blue-500/20">Réessayer</button>
            </div>
          ) : (
            <>
              {/* Vue Table Desktop */}
              {viewMode === 'table' && (
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10 text-left text-sm text-slate-400">
                        {[
                          { key: 'company_name', label: 'Entreprise', icon: FiHome },
                          { key: 'nif', label: 'NIF' },
                          { key: 'rccm', label: 'RCCM' },
                          { key: null, label: 'Adresse' },
                          { key: 'invoice_count', label: 'Factures' },
                          { key: 'total_ht', label: 'Total HT' },
                          { key: 'tax_amount', label: 'TVA' },
                          { key: 'total_ttc', label: 'Total TTC' },
                        ].map(({ key, label, icon: Icon }) => (
                          <th
                            key={key || label}
                            onClick={() => key && handleSort(key)}
                            className={`p-4 ${key ? 'cursor-pointer hover:text-white transition-colors group' : ''}`}
                          >
                            <div className="flex items-center gap-2">
                              {Icon && <Icon size={14} />}
                              <span>{label}</span>
                              {key && (
                                <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                                  {sortConfig.key === key ? (
                                    sortConfig.direction === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />
                                  ) : (
                                    <FiArrowUp size={14} className="text-slate-600" />
                                  )}
                                </span>
                              )}
                            </div>
                          </th>
                        ))}
                        <th className="p-4 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentCompanies.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="p-8 text-center text-slate-500">Aucune entreprise trouvée</td>
                        </tr>
                      ) : (
                        currentCompanies.map((company, index) => (
                          <motion.tr
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.02 }}
                            className="border-b border-white/5 hover:bg-white/5 transition-colors text-sm"
                          >
                            <td className="p-4 font-medium text-white">
                              <div className="flex items-center gap-2">
                                <FiHome className="text-blue-400" size={14} />
                                {company.company_name}
                              </div>
                            </td>
                            <td className="p-4 text-slate-300">{company.nif || '—'}</td>
                            <td className="p-4 text-slate-300">{company.rccm || '—'}</td>
                            <td className="p-4 text-slate-300 max-w-[200px] truncate">{company.address || '—'}</td>
                            <td className="p-4 text-slate-300 text-right">{formatNumber(company.invoice_count)}</td>
                            <td className="p-4 text-slate-300 text-right">{formatCurrency(company.total_ht)}</td>
                            <td className="p-4 text-amber-400 text-right">{formatCurrency(company.tax_amount)}</td>
                            <td className="p-4 text-emerald-400 text-right font-bold">{formatCurrency(company.total_ttc)}</td>
                            <td className="p-4 text-center">
                              <button
                                onClick={() => { setSelectedCompany(company); setShowModal(true); }}
                                className="p-2 rounded-xl bg-blue-500/10 border border-blue-400/20 text-blue-400 hover:bg-blue-500/20 transition-colors"
                              >
                                <FiEye size={16} />
                              </button>
                            </td>
                          </motion.tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Vue Grille Desktop (optionnelle) ou Mobile cartes */}
              {(viewMode === 'grid' || isMobile) && (
                <div className={`${isMobile ? '' : 'hidden md:grid grid-cols-2 lg:grid-cols-3 gap-4'} p-4`}>
                  {currentCompanies.length === 0 ? (
                    <div className="col-span-full text-center py-10 text-slate-400">Aucune entreprise trouvée</div>
                  ) : (
                    currentCompanies.map((company, index) => (
                      <CompanyCardMobile
                        key={index}
                        company={company}
                        onClick={(c) => { setSelectedCompany(c); setShowModal(true); }}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 border-t border-white/10">
                  <div className="text-sm text-slate-400">
                    {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, filteredCompanies.length)} sur {filteredCompanies.length}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30 hover:bg-white/10"><FiChevronsLeft size={16} /></button>
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30 hover:bg-white/10"><FiChevronLeft size={16} /></button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;
                      if (pageNum >= 1 && pageNum <= totalPages) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-8 h-8 rounded-lg text-sm font-medium ${
                              currentPage === pageNum
                                ? 'bg-blue-500/20 text-blue-400 border border-blue-400/30'
                                : 'text-slate-400 hover:bg-white/10'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                      return null;
                    })}
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30 hover:bg-white/10"><FiChevronRight size={16} /></button>
                    <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30 hover:bg-white/10"><FiChevronsRight size={16} /></button>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <span>Afficher</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                      className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-white"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>

      {/* Modal détails */}
      <AnimatePresence>
        {showModal && selectedCompany && (
          <CompanyDetailsModal company={selectedCompany} onClose={() => setShowModal(false)} />
        )}
      </AnimatePresence>

      {/* Menus mobiles */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        onRefresh={manualRefresh}
        onClearCache={clearCacheAndReload}
        onExport={exportToCSV}
        onResetFilters={resetFilters}
        isRefreshing={isRefreshing}
      />
      <MobileFilters
        isOpen={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        minAmount={minAmount}
        setMinAmount={setMinAmount}
        maxAmount={maxAmount}
        setMaxAmount={setMaxAmount}
        onApply={() => setCurrentPage(1)}
      />

      {/* Indicateur de connexion */}
      <div className={`fixed bottom-4 right-4 z-50 px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 backdrop-blur-xl border ${
        isOnline ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
      }`}>
        <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
        <span>{isOnline ? 'En ligne' : 'Hors ligne'}</span>
      </div>
    </div>
  );
};

export default CompanySummary;